/**
 * The microphone, while the desktop shell's global key is what governs it.
 *
 * §5.2 of the Fase 6 design: the shell registers a key with Windows, works out
 * pressing and releasing from the auto-repeat, and says over IPC which of the
 * two just happened. Everything hard about that lives in the shell. What is
 * left for the page is one decision — given what the shell just said and what
 * held until now, is the microphone open or shut — and it is written here, on
 * its own, so it can be checked without a room, a browser or a keyboard.
 */

export interface PushToTalkState {
  /**
   * Whether the key is what governs the microphone now.
   *
   * The page cannot ask the shell whether push-to-talk is switched on: the
   * bridge of §6 carries a single boolean in this direction and nothing else.
   * What it can do is believe the shell when it speaks. A shell with the
   * feature off never sends anything — it never registers a key — so silence
   * means the microphone stays exactly as it is today, which is also what a
   * plain browser gets.
   */
  readonly armed: boolean;
  /** Whether the microphone should be open. Only means anything once armed. */
  readonly open: boolean;
}

/** Nobody has spoken yet: the page is on its own, as in any browser. */
export const IDLE_PUSH_TO_TALK: PushToTalkState = { armed: false, open: false };

/**
 * What the microphone becomes when the shell says the key went down or came up.
 *
 * The first word from the shell hands it the microphone and never gives it
 * back: releasing the key closes the microphone, it does not return the page
 * to its old behaviour — a microphone that reopened by itself on release
 * would be the exact opposite of a push-to-talk.
 *
 * An announcement that changes nothing returns the very same state, so a shell
 * repeating itself does not make the component ask the room for something that
 * is already true.
 */
export function nextPushToTalk(state: PushToTalkState, talking: boolean): PushToTalkState {
  if (state.armed && state.open === talking) return state;

  return { armed: true, open: talking };
}
